import { AccountType, RateType } from '@treadinc/horizon-api-spec';
import { RefObject, useCallback } from 'react';

import { AddOnAssignment } from '~hooks/useAddOns/models';
import { RateBasicWithValue } from '~hooks/useRates/models';
import { SiteRateSchedule } from '~hooks/useSiteRateSchedule/models';
import {
  AddOnAssignmentSchema,
  constructDefaultValues,
} from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { RateDetailsHandler } from '~pages/Sales/Orders/NewOrderForm/RateDetails';

/**
 * Imperative contract the schedule picker flow requires from a per-party rate
 * input: on top of `RateDetailsHandler#onSubmit`, it must accept a rate trio
 * and an add-on list pushed from outside so that picking a schedule can stamp
 * the appropriate rate and add-ons per side. `JobRateInput` is the component
 * that implements this (see `JobRateInput.tsx`); the legacy `RateDetails`
 * component only needs the submit half of the contract.
 */
export interface JobRateInputHandler extends RateDetailsHandler {
  validateCommission?: () => boolean;
  setCommissionError?: (message: string) => void;
  applyRate: (trio: {
    targetRate: RateBasicWithValue | null;
    targetRateType: RateType | null;
    targetRateValue: number | null;
  }) => void;
  applyAddOns: (addOns: AddOnAssignmentSchema[]) => void;
}

type Side = 'customer' | 'vendor' | 'driver';

type RateTrio = {
  targetRate: RateBasicWithValue | null;
  targetRateType: RateType | null;
  targetRateValue: number | null;
};

/**
 * Pulls the rate/rateType/rateValue trio for one side out of a SiteRateSchedule.
 * Keeps the field-name concatenation in one place so callers don't repeat the
 * `schedule.customerRate / customerRateType / customerRateValue` pattern.
 */
export const trioFromSchedule = (side: Side, schedule: SiteRateSchedule): RateTrio => {
  const rate = schedule[`${side}Rate` as const] ?? null;
  const rateType = schedule[`${side}RateType` as const] ?? null;
  const rateValue = schedule[`${side}RateValue` as const] ?? null;
  return {
    targetRate: rate,
    targetRateType: rateType,
    targetRateValue: rateValue,
  };
};

export const rateTrioDiverges = (a: RateTrio, b: RateTrio): boolean =>
  (a.targetRate?.id ?? null) !== (b.targetRate?.id ?? null) ||
  (a.targetRateType ?? null) !== (b.targetRateType ?? null) ||
  Number(a.targetRateValue ?? 0) !== Number(b.targetRateValue ?? 0);

/**
 * The auto-add-on endpoint returns a flat list keyed by account_type:
 * customer/vendor are set explicitly; driver rows have account_type = null
 * per the BE's SetAutoAddOns logic. Partition by side so each can be handed
 * to the matching RateDetails ref.
 */
const partitionAddOns = (
  addOns: AddOnAssignment[],
  assigneeSide: 'vendor' | 'driver',
): { customer: AddOnAssignment[]; assignee: AddOnAssignment[] } => {
  const customer = addOns.filter((a) => a.accountType === AccountType.CUSTOMER);
  const assignee =
    assigneeSide === 'vendor'
      ? addOns.filter((a) => a.accountType === AccountType.VENDOR)
      : addOns.filter((a) => a.accountType === null || a.accountType === undefined);
  return { customer, assignee };
};

interface UseJobScheduleRatesArgs {
  /**
   * Read the current `siteRateSchedule` form value. The parent wraps its
   * typed `getValues` so this hook doesn't need to know the full form type.
   */
  getSchedule: () => SiteRateSchedule | null;
  /**
   * Write the `siteRateSchedule` form value. The parent wraps its typed
   * `setValue` for the same reason.
   */
  setSchedule: (schedule: SiteRateSchedule | null) => void;
  isAssignedToVendor: boolean;
  customerRateDetailsRef: RefObject<JobRateInputHandler>;
  vendorOrDriverRateDetailsRef: RefObject<JobRateInputHandler>;
}

interface UseJobScheduleRatesReturn {
  applySchedule: (schedule: SiteRateSchedule, addOns: AddOnAssignment[]) => void;
  clearSchedule: () => void;
  unlinkSchedule: () => void;
  onCustomerRateFieldsChange: (values: RateTrio) => void;
  onAssigneeRateFieldsChange: (values: RateTrio) => void;
}

/**
 * Owns the job form's site-rate-schedule orchestration: applying a schedule's
 * rates + add-ons to the two RateDetails handles, clearing the stamp, and
 * detecting rate-field divergence so any edit invalidates the schedule link.
 *
 * The BE stores a single `site_rate_schedule_id` on Job, so any divergence on
 * either side clears the one stamp we track on the form (`siteRateSchedule`).
 * Non-rate edits (material, equipment, etc.) don't clear the stamp — they stay
 * put and may trigger a server-side auto-match on save.
 *
 * Refs stay in the parent so the existing submit path (which calls
 * `ref.current?.onSubmit(...)`) keeps working unchanged.
 */
export default function useJobScheduleRates({
  getSchedule,
  setSchedule,
  isAssignedToVendor,
  customerRateDetailsRef,
  vendorOrDriverRateDetailsRef,
}: UseJobScheduleRatesArgs): UseJobScheduleRatesReturn {
  const assigneeSide: 'vendor' | 'driver' = isAssignedToVendor ? 'vendor' : 'driver';

  const applySchedule = useCallback(
    (schedule: SiteRateSchedule, addOns: AddOnAssignment[]) => {
      setSchedule(schedule);

      customerRateDetailsRef.current?.applyRate(trioFromSchedule('customer', schedule));
      vendorOrDriverRateDetailsRef.current?.applyRate(
        trioFromSchedule(assigneeSide, schedule),
      );

      const { customer, assignee } = partitionAddOns(addOns, assigneeSide);
      // Pass `shouldCopyOriginalAutoAddOnAssignmentId: true` so the SRS-sourced
      // add-ons keep their link back to the SiteRateSchedule add-on row. Without
      // this, the BE persists them with `original_auto_add_on_assignment_id = nil`,
      // which makes `JobService::SetMatchingRateOnAssignment#set_auto_add_ons!`
      // skip them on the next rate-card switch (TRE-14276).
      customerRateDetailsRef.current?.applyAddOns(
        constructDefaultValues(customer, true, true).addOns,
      );
      vendorOrDriverRateDetailsRef.current?.applyAddOns(
        constructDefaultValues(assignee, true, true).addOns,
      );
    },
    [setSchedule, assigneeSide, customerRateDetailsRef, vendorOrDriverRateDetailsRef],
  );

  const clearSchedule = useCallback(() => {
    setSchedule(null);

    // Mirror `applySchedule`: wipe the rate trio and tombstone any add-ons on
    // both sides so clearing the system-matched rate card removes the values
    // it stamped. `applyAddOns([])` reuses the same tombstone-then-append
    // semantics the apply path uses, ensuring previously-persisted add-ons
    // get DELETEd on submit instead of silently sticking around.
    const emptyTrio = {
      targetRate: null,
      targetRateType: null,
      targetRateValue: null,
    };
    customerRateDetailsRef.current?.applyRate(emptyTrio);
    vendorOrDriverRateDetailsRef.current?.applyRate(emptyTrio);
    customerRateDetailsRef.current?.applyAddOns([]);
    vendorOrDriverRateDetailsRef.current?.applyAddOns([]);
  }, [setSchedule, customerRateDetailsRef, vendorOrDriverRateDetailsRef]);

  // Unlinks the schedule reference without touching rate trios or add-ons.
  // Used by error-recovery paths (e.g. a transient add-on fetch failure when
  // the user picks a new schedule) where wiping pre-existing rate data would
  // destroy user input the user never asked to clear.
  const unlinkSchedule = useCallback(() => {
    setSchedule(null);
  }, [setSchedule]);

  const makeDivergenceWatcher = useCallback(
    (extractParty: (schedule: SiteRateSchedule) => RateTrio) => (values: RateTrio) => {
      const linked = getSchedule();
      if (!linked) return;
      if (rateTrioDiverges(values, extractParty(linked))) {
        setSchedule(null);
      }
    },
    [getSchedule, setSchedule],
  );

  const onCustomerRateFieldsChange = useCallback(
    (values: RateTrio) =>
      makeDivergenceWatcher((s) => trioFromSchedule('customer', s))(values),
    [makeDivergenceWatcher],
  );

  const onAssigneeRateFieldsChange = useCallback(
    (values: RateTrio) =>
      makeDivergenceWatcher((s) => trioFromSchedule(assigneeSide, s))(values),
    [makeDivergenceWatcher, assigneeSide],
  );

  return {
    applySchedule,
    clearSchedule,
    unlinkSchedule,
    onCustomerRateFieldsChange,
    onAssigneeRateFieldsChange,
  };
}
